import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineHome,
  HiOutlineUsers,
  HiOutlinePhone,
  HiOutlineCalendar,
  HiOutlineUserGroup,
  HiOutlineChartBar,
  HiOutlineCube,
  HiOutlineCog,
  HiOutlineArrowRight,
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineCheckCircle,
  HiOutlineSparkles,
} from 'react-icons/hi';
import logo from '../assets/logo.png';

const DISMISSED_KEY = 'televalley_quick_start_dismissed';

const STEPS = [
  {
    key: 'welcome',
    type: 'welcome',
  },
  {
    key: 'dashboard',
    type: 'feature',
    title: 'Dashboard Analytics',
    description: 'Your command center for sales performance — see everything at a glance.',
    icon: HiOutlineHome,
    gradient: 'from-primary-500 to-primary-600',
    ringColor: 'ring-primary-500/20',
    bgLight: 'bg-primary-50',
    textColor: 'text-primary-600',
    route: '/app/',
    highlights: [
      'View real-time sales metrics and KPIs at a glance',
      'Track call performance with interactive charts and trends',
      'Monitor your pipeline funnel and CEO-level alerts',
      'Filter by date range, source, and product for deep insights',
    ],
  },
  {
    key: 'leads',
    type: 'feature',
    title: 'Lead Management',
    description: 'Add, import, and organize leads through your custom pipeline stages.',
    icon: HiOutlineUsers,
    gradient: 'from-blue-500 to-blue-600',
    ringColor: 'ring-blue-500/20',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
    route: '/app/leads',
    highlights: [
      'Add leads manually or import via CSV',
      'Move leads through custom pipeline stages',
      'Filter, search, and perform bulk actions',
      'Assign leads to telecallers for focused outreach',
    ],
  },
  {
    key: 'calls',
    type: 'feature',
    title: 'Call Tracking',
    description: 'Monitor call logs, recordings, and connection rates for your team.',
    icon: HiOutlinePhone,
    gradient: 'from-green-500 to-emerald-600',
    ringColor: 'ring-green-500/20',
    bgLight: 'bg-green-50',
    textColor: 'text-green-600',
    route: '/app/call-logs',
    highlights: [
      'View all call logs with detailed filters',
      'Listen to call recordings directly in the browser',
      'Track connection rates and conversion metrics',
      'Export call data for offline analysis',
    ],
  },
  {
    key: 'followups',
    type: 'feature',
    title: 'Follow-ups',
    description: 'Schedule and track follow-ups so no lead slips through the cracks.',
    icon: HiOutlineCalendar,
    gradient: 'from-amber-500 to-orange-600',
    ringColor: 'ring-amber-500/20',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-600',
    route: '/app/follow-ups',
    highlights: [
      "View today's pending follow-ups at a glance",
      'Schedule callbacks with date and time reminders',
      'Track completion status across all follow-ups',
      'Auto-escalate overdue follow-ups so nothing is missed',
    ],
  },
  {
    key: 'users',
    type: 'feature',
    title: 'User Management',
    description: 'Add telecallers, set call targets, and monitor individual performance.',
    icon: HiOutlineUserGroup,
    gradient: 'from-purple-500 to-violet-600',
    ringColor: 'ring-purple-500/20',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-600',
    route: '/app/users',
    highlights: [
      'Add & manage telecaller accounts',
      'Set daily and weekly call targets per user',
      'Track individual call performance and productivity',
      'Role-based access control for admins and managers',
    ],
  },
  {
    key: 'reports',
    type: 'feature',
    title: 'Reports & Insights',
    description: 'Daily work reports, leaderboard rankings, and attendance tracking.',
    icon: HiOutlineChartBar,
    gradient: 'from-rose-500 to-pink-600',
    ringColor: 'ring-rose-500/20',
    bgLight: 'bg-rose-50',
    textColor: 'text-rose-600',
    route: '/app/reports',
    highlights: [
      'Daily work summary reports for the entire team',
      'Leaderboard rankings to motivate top performers',
      'Attendance tracking and leave management',
      'Export reports to CSV for further analysis',
    ],
  },
  {
    key: 'products',
    type: 'feature',
    title: 'Products & Scripts',
    description: 'Manage your product catalog and call scripts for consistent outreach.',
    icon: HiOutlineCube,
    gradient: 'from-teal-500 to-cyan-600',
    ringColor: 'ring-teal-500/20',
    bgLight: 'bg-teal-50',
    textColor: 'text-teal-600',
    route: '/app/products',
    highlights: [
      'Create & manage your product catalog',
      'Build call scripts for consistent conversations',
      'Link scripts to specific products',
      'Quick-access scripts during active calls',
    ],
  },
  {
    key: 'settings',
    type: 'feature',
    title: 'Settings & Integrations',
    description: 'Configure WhatsApp templates, Google Sheets sync, pipeline stages, and more.',
    icon: HiOutlineCog,
    gradient: 'from-slate-500 to-gray-600',
    ringColor: 'ring-slate-500/20',
    bgLight: 'bg-slate-50',
    textColor: 'text-slate-600',
    route: '/app/settings',
    highlights: [
      'WhatsApp message templates for automated outreach',
      'Google Sheets sync for lead data management',
      'Custom pipeline stages to match your sales process',
      'Webhook integrations to connect with your CRM',
    ],
  },
  {
    key: 'complete',
    type: 'complete',
  },
];

const QUICK_FEATURES = STEPS.filter(s => s.type === 'feature');

const QuickStartGuide = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!isOpen) return null;

  const step = STEPS[currentStep];
  const totalSteps = STEPS.length;
  const progress = ((currentStep) / (totalSteps - 1)) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setCurrentStep(totalSteps - 1); // Jump to complete step
  };

  const handleFeatureClick = (route) => {
    if (dontShowAgain) {
      localStorage.setItem(DISMISSED_KEY, 'true');
    }
    onClose();
    navigate(route);
  };

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(DISMISSED_KEY, 'true');
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // ─── Welcome Step ─────────────────────────────────────────
  if (step.type === 'welcome') {
    return (
      <div className="quick-start-guide fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={handleBackdropClick}>
        <div className="fixed inset-0 bg-gradient-to-br from-secondary-900/60 to-secondary-800/40 backdrop-blur-sm" />
        <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-secondary-200/50 overflow-hidden animate-scale-in">
          {/* Close button */}
          <button onClick={handleClose} className="absolute top-4 right-4 z-10 p-2 rounded-xl text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors" aria-label="Close">
            <HiOutlineX className="w-5 h-5" />
          </button>

          {/* Gradient Header */}
          <div className="relative px-6 pt-8 pb-6 bg-gradient-to-r from-primary-600 via-primary-500 to-indigo-500 overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-12 translate-x-12" />
            <div className="absolute bottom-0 left-1/4 w-28 h-28 bg-white/5 rounded-full translate-y-8" />
            <div className="relative flex flex-col items-center text-center">
              <div className="brand-logo-box w-64 p-2 rounded-xl bg-white backdrop-blur-sm flex items-center justify-center shadow-lg ring-2 ring-white/30 mb-4">
                <img src={logo} alt="TeleValley" className="object-contain" />
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">
                Welcome to TeleValley!
              </h2>
              <p className="text-sm text-white/80 mt-2 max-w-sm">
                Your all-in-one telecalling management platform. Let's take a quick tour of everything you can do.
              </p>
            </div>
          </div>

          {/* Feature preview icons */}
          <div className="px-6 py-6">
            <div className="flex justify-center gap-3 mb-6">
              {QUICK_FEATURES.slice(0, 4).map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.key} className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.gradient} ring-2 ${f.ringColor} flex items-center justify-center shadow-md`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center gap-3 mb-6">
              {QUICK_FEATURES.slice(4).map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.key} className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.gradient} ring-2 ${f.ringColor} flex items-center justify-center shadow-md`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                );
              })}
            </div>

            {/* Progress */}
            <p className="text-center text-xs text-secondary-400 mb-4">Step 1 of {totalSteps}</p>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <button
                onClick={handleNext}
                className="w-full px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 shadow-md shadow-primary-500/25 hover:shadow-lg rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
              >
                <HiOutlineSparkles className="w-4 h-4" />
                Start Tour
              </button>
              <button
                onClick={handleSkip}
                className="w-full px-5 py-2 text-sm font-medium text-secondary-500 hover:text-secondary-700 hover:bg-secondary-50 rounded-xl transition-colors duration-200"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Feature Step ─────────────────────────────────────────
  if (step.type === 'feature') {
    const Icon = step.icon;

    return (
      <div className="quick-start-guide fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={handleBackdropClick}>
        <div className="fixed inset-0 bg-gradient-to-br from-secondary-900/60 to-secondary-800/40 backdrop-blur-sm" />
        <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-secondary-200/50 overflow-hidden animate-scale-in">
          {/* Progress Bar */}
          <div className="h-1.5 bg-secondary-100">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-r-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Close button */}
          <button onClick={handleClose} className="absolute top-4 right-4 z-10 p-2 rounded-xl text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors" aria-label="Close">
            <HiOutlineX className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="px-6 pt-6 pb-4">
            {/* Step indicator */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold text-secondary-400 uppercase tracking-wider">
                Step {currentStep + 1} of {totalSteps}
              </p>
              <button
                onClick={() => handleFeatureClick(step.route)}
                className="flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors group"
              >
                Go to {step.title}
                <HiOutlineArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>

            {/* Feature icon + title */}
            <div className="flex flex-col sm:flex-row items-start gap-5 mb-5">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.gradient} ring-4 ${step.ringColor} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-secondary-900 mb-1">
                  {step.title}
                </h3>
                <p className="text-sm text-secondary-500 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>

            {/* Highlights */}
            <div className={`${step.bgLight} rounded-xl p-4 mb-5`}>
              <p className="text-xs font-semibold text-secondary-700 uppercase tracking-wider mb-3">What you can do</p>
              <ul className="space-y-2.5">
                {step.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <HiOutlineCheck className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-secondary-700 leading-snug">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Step dots */}
            <div className="flex items-center justify-center gap-1.5 mb-4">
              {STEPS.map((s, idx) => (
                <div
                  key={s.key}
                  className={`rounded-full transition-all duration-300 ${
                    idx === currentStep
                      ? 'w-6 h-2 bg-primary-500'
                      : idx < currentStep
                        ? 'w-2 h-2 bg-primary-300'
                        : 'w-2 h-2 bg-secondary-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Footer with navigation */}
          <div className="flex items-center justify-between px-6 py-4 bg-secondary-50/50 border-t border-secondary-100">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-xl transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <HiOutlineChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 shadow-md shadow-primary-500/25 hover:shadow-lg rounded-xl transition-all duration-200"
            >
              {currentStep === totalSteps - 2 ? 'Finish' : 'Next'}
              <HiOutlineChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Complete Step ────────────────────────────────────────
  if (step.type === 'complete') {
    return (
      <div className="quick-start-guide fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={handleBackdropClick}>
        <div className="fixed inset-0 bg-gradient-to-br from-secondary-900/60 to-secondary-800/40 backdrop-blur-sm" />
        <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-secondary-200/50 overflow-hidden animate-scale-in max-h-[90vh] overflow-y-auto">
          {/* Close button */}
          <button onClick={handleClose} className="absolute top-4 right-4 z-10 p-2 rounded-xl text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors" aria-label="Close">
            <HiOutlineX className="w-5 h-5" />
          </button>

          {/* Progress Bar */}
          <div className="h-1.5 bg-primary-500" />

          {/* Celebration Header */}
          <div className="px-6 pt-8 pb-4 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto shadow-lg ring-4 ring-green-500/20 mb-4">
              <HiOutlineCheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-secondary-900 mb-1">
              You're all set!
            </h2>
            <p className="text-sm text-secondary-500 max-w-md mx-auto">
              You now know the key features of TeleValley. Click any feature below to jump right in, or close this guide to start exploring on your own.
            </p>
          </div>

          {/* Feature Grid Summary */}
          <div className="px-6 pb-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {QUICK_FEATURES.map((feature) => {
                const FIcon = feature.icon;
                return (
                  <button
                    key={feature.key}
                    onClick={() => handleFeatureClick(feature.route)}
                    className="group flex flex-col items-center p-3 rounded-xl border-2 border-secondary-100 bg-white hover:border-primary-200 hover:bg-primary-50/30 hover:shadow-md transition-all duration-200 text-center"
                  >
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${feature.gradient} ring-2 ${feature.ringColor} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-200`}>
                      <FIcon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-xs font-semibold text-secondary-900 group-hover:text-primary-700 transition-colors leading-tight">
                      {feature.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 bg-secondary-50/50 border-t border-secondary-100">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
              />
              <span className="text-sm text-secondary-600">
                Don't show this guide again
              </span>
            </label>
            <button
              onClick={handleClose}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 shadow-md shadow-primary-500/25 hover:shadow-lg rounded-xl transition-all duration-200"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default QuickStartGuide;
export { DISMISSED_KEY };